<template>
  <b-modal id="task-show-modal"
           :title="task ? task.title : $t('task')"
           size="lg"
           @hidden="onHide"
           @show="onShow"
           centered>
    <div v-if="task">
      <div v-if="!task.onlyComments">
        <div class="row justify-content-between">
          <div class="mb-3 col col-auto">
            <label class="form-label">{{ $t('type') }}</label>
            <div>{{ $t(task.task_type) }}</div>
          </div>
          <div class="mb-3 col col-auto">
            <div>
              <label class="form-label">{{ $t('importance') }}</label>
              <div>
                <CRMDatatableTDTaskType :object="task"
                                        fieldType="importance"/>
              </div>
            </div>
          </div>
          <div class="mb-3 col col-auto" v-if="task.start">
            <div>
              <label class="form-label">{{ $t('start_period') }}</label>
              <div>{{ formatDateTime(task.start) }}</div>
            </div>
          </div>
          <div class="mb-3 col col-auto">
            <div>
              <label class="form-label">{{ $t('deadline') }}</label>
              <div>{{ task.deadline ? formatDateTime(task.deadline) : '-' }}</div>
            </div>
          </div>
          <div class="mb-3 col-12" v-if="task.patient">
            <div>
              <label class="form-label">{{ $t('patient') }}</label>
              <div class="d-flex align-items-center">
                <!--                <a @click="toPatientPage" class="text-primary pointer">{{ task.patient.full_name }}</a>-->
                <router-link :to="{ name: 'PatientVisits', params: { id: task.patient_id }}"
                             @click="$bvModal.hide('task-show-modal')"
                             class="text-primary pointer">
                  {{ task.patient.full_name }}
                </router-link>
                <span v-if="phone && !rights.includes('hide_patient_phone')"
                      class="text-primary">
                  ({{ phone }})
                </span>
                <a class="ml-3"
                   v-if="!rights.includes('hide_patient_phone')"
                   :href="`tel:${phone}`"
                   v-b-tooltip.hover
                   :title="$t('call')">
                  <PhoneSvg class="svg-primary"/>
                </a>
                <a v-if="clinic && clinic.sms_info && rights.includes('sms_send') && !rights.includes('hide_patient_phone')"
                   v-b-tooltip.hover
                   :title="$t('send_sms')"
                   class="ml-3 pointer"
                   @click="smsModal(phone)">
                  <SmsSvg class="svg-primary"/>
                </a>

                <WhatsappTelegramIcon v-if="patient && !rights.includes('hide_patient_phone')"
                                      :top-position="0"
                                      :patient-id="patient.id"
                                      :phone="patient.phone"/>

                <b-dropdown variant="link" v-if="rights.includes('crm')" class="ml-3">
                  <template #button-content>
                    <a v-b-tooltip.hover
                       class="pointer"
                       :title="$t('add_to_funnel')">
                      <FilterSvg class="svg-primary svg-size-lg"/>
                    </a>
                  </template>
                  <b-dropdown-item @click="addToLeads">
                    {{ $t('applications_and_appeals') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="addToPatientCanban">
                    {{ $t('funnel_came_and_repeated') }}
                  </b-dropdown-item>
                </b-dropdown>


              </div>
            </div>
          </div>
        </div>
        <div v-if="task.lead" class="mb-3">
          <a class="pointer"
             @click="leadClicked">
            {{ $t('lead') }}#{{ task.lead_id }}
          </a>
        </div>
        <hr class="mt-0"/>
        <div class="row">
          <div class="col">
            <label class="form-label">{{ $t('status') }}</label>
            <div>
              <SelectGroup :options="taskStatuses"
                           v-model="task.status"
                           @input="changeStatus"
                           variant="white"
                           class="md"
                           :showlabel="false"
                           :label="$t('status')"/>
            </div>
          </div>
          <div class="col" v-if="task.user">
            <label class="form-label">{{ $t('responsible') }}</label>
            <div>{{ task.user.full_name }}</div>
          </div>
          <div class="col" v-if="task.author">
            <label class="form-label">{{ $t('author') }}</label>
            <div>{{ task.author.full_name }}</div>
          </div>
        </div>
        <div class="mt-3" v-if="task.description">
          <label class="form-label black">{{ $t('description') }}</label>
          <div>{{ task.description }}</div>
        </div>
        <div class="mt-3" v-if="task.files && task.files.length">
          <div v-for="(t, tind) in task.files" :key="`file-${tind}`">
            <a v-if="t.original" :href="t.fileUrl" target="_blank">
              {{ t.original }}
            </a>
          </div>
        </div>
      </div>
      <GeneralCommentsBlock :object="task"
                            class="mt-4"
                            model="task_comment"
                            parent-column="task_id"/>
    </div>
    <template #modal-footer="{ close }">
      <div class="modal-footer-buttons no-print">
        <b-button class="cancel mr-md-3 mr-2 px-3 d-inline-block align-middle w-auto"
                  variant="themed"
                  @click="edit">
          {{ $t('edit') }}
        </b-button>
        <button :disabled="loading"
                @click="close()"
                class="btn-themed align-middle btn-themed-squared">
          {{ $t('done') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>

import { mapState } from "vuex"
import CRMDatatableTDTaskType from "@/components/parts/datatable/td/crm/CRMDatatableTDTaskType"
import JwtService from "@/services/jwt.service"
import GeneralCommentsBlock from "@/components/parts/general/GeneralCommentsBlock"
import { formatDateTime, formatPhone } from "@/extensions/filters/filters"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { CRMService } from "@/services/api/crm.api.service"
import {CalendarService} from "@/services/api/calendar.api.service"
import SelectGroup from "@/components/form/SelectGroup.vue"
import { taskStatuses } from "@/dictionaries/dictionary"
import PhoneSvg from "@/assets/svg-vue/header/phone_t.svg.vue"
import WhatsappTelegramIcon from "@/components/pages/patients/WhatsappTelegramIcon.vue"
import SmsSvg from "@/assets/svg-vue/general/sms_t.svg.vue"
import FilterSvg from "@/assets/svg-vue/sidebar/crm.svg.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"

export default {
  name: "TaskShowModal",
  data() {
    return {
      taskStatuses,
      selectedPhone: null
    }
  },
  components: {
    SmsSvg,
    FilterSvg,
    WhatsappTelegramIcon,
    PhoneSvg,
    SelectGroup,
    GeneralCommentsBlock,
    CRMDatatableTDTaskType
  },
  computed: {
    ...mapState({
      clinic: state => state.auth.clinic,
      loading: state => state.dom.loading,
      task: state => state.crm.task,
      rights: state => state.auth.rights,
    }),
    preUrl() {
      let link = JwtService.getPrintPrelink(this.clinic)
      return link+'/task/file/'
    },
    phone() {
      return formatPhone(this.task.phone || this.task.patient?.phone)
    },
    patient() {
      return this.task.patient
    }
  },
  methods: {
    formatDateTime,
    edit() {
      this.$bvModal.hide('task-show-modal')
      let newObject = JSON.parse(JSON.stringify(this.task))
      this.$store.commit('setModule', 'crm')
      this.$store.commit('setSelectedModel', newObject)
      this.$store.commit('setViewModel', 'task')
      this.$nextTick(() => {
        this.$store.commit('setShowModal', true)
      })
    },
    async changeStatus(status) {
      let res = await CalendarService.finishTask(this.task.id, status)
      if(res.data) this.$store.commit('incrementDataTableCounter')
    },
    async smsModal() {
      this.$store.commit('setPatient', this.patient)
      this.$bvModal.show('patient-sms-modal')
    },
    onHide() {
      const queryParams = { ...this.$route.query }
      if(queryParams.id) {
        delete queryParams.id
        this.$router.replace({ query: queryParams })
      }
    },
    async onShow() {
      const res = await EloquentService.show('task', this.task.id)
      this.$store.commit('setTask', copyObject(res.data))
    },
    async leadClicked() {
      if(!this.task.lead_id) return
      this.$bvModal.hide('task-show-modal')
      const leadFull = await EloquentService.show('lead', this.task.lead_id)
      this.$store.commit('setLead', copyObject(leadFull.data))
      this.$bvModal.show('lead-show-modal')
    },
    async addToLeads() {
      await this.createCanbanFromTask('lead')
    },
    async addToPatientCanban() {
      await this.createCanbanFromTask('patient_canban')
    },
    async createCanbanFromTask(type) {
      try {
        const res = await CRMService.createCanbanFromTask({
          task_id: this.task.id,
          type
        })
        if(res?.data) {
          this.$noty.info(this.$t('success_message'))
        }
      } catch (e) {
        console.log(e)
      }
    },
  },
}
</script>
